import { useState } from 'react';
import { Global, css } from '@emotion/react';
import { Popover } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { ExclamationCircleFilled } from '@ant-design/icons';

import { colorConstants } from 'config/colorConstants';

import { CustomCheckbox } from './PackArchitectureTable.styles';
import {
    MAX_KPI_SELECTION,
    TABLE_MAX_METRIC_SELECTION_MESSAGE,
    TABLE_MIN_METRIC_SELECTION_MESSAGE,
} from './packArchitectureTable.helpers';
import type { KpiOption } from './packArchitectureTable.helpers';

const METRIC_SELECTION_POPOVER_OVERLAY_CLASS = 'pa-metric-selection-popover';

const MAX_METRIC_HOVER_ENTER_DELAY_SECONDS = 0.3;
const MAX_METRIC_HOVER_LEAVE_DELAY_SECONDS = 0.1;

const metricSelectionPopoverGlobalStyles = css`
    .${METRIC_SELECTION_POPOVER_OVERLAY_CLASS} .ant-popover-inner-content {
        padding: 0;
    }
`;

type PackArchitectureTableKpiCheckboxProps = {
    option: KpiOption;
    isChecked: boolean;
    selectedKpiCount: number;
    onToggle: (kpiId: string) => void;
    'data-testid'?: string;
};

const MinMetricSelectionPopoverContent = ({ onClose }: { onClose: () => void }) => (
    <div className="flex w-[248px] flex-col gap-2">
        <div className="flex items-start gap-2">
            <ExclamationCircleFilled
                className="mt-0.5 shrink-0 text-base"
                style={{ color: colorConstants.growthDown }}
            />
            <span className="text-sm font-normal leading-[22px]" style={{ color: colorConstants.blackTitle }}>
                {TABLE_MIN_METRIC_SELECTION_MESSAGE}
            </span>
        </div>
        <div className="flex justify-end">
            <button
                type="button"
                className="flex h-6 items-center border-0 bg-transparent p-0 text-sm font-normal leading-[22px]"
                style={{ color: colorConstants.violet }}
                onClick={onClose}
            >
                Close
            </button>
        </div>
    </div>
);

const MaxMetricSelectionPopoverContent = () => (
    <div className="flex w-[248px] items-start gap-2">
        <ExclamationCircleFilled
            className="mt-0.5 shrink-0 text-base"
            style={{ color: colorConstants.growthDown }}
        />
        <span className="text-sm font-normal leading-[22px]" style={{ color: colorConstants.blackTitle }}>
            {TABLE_MAX_METRIC_SELECTION_MESSAGE}
        </span>
    </div>
);

export const PackArchitectureTableKpiCheckbox = ({
    option,
    isChecked,
    selectedKpiCount,
    onToggle,
    'data-testid': testId,
}: PackArchitectureTableKpiCheckboxProps) => {
    const [showMinMetricPopover, setShowMinMetricPopover] = useState(false);

    const isMaxSelectionReached = !isChecked && selectedKpiCount >= MAX_KPI_SELECTION;

    const handleChange = (nextChecked: boolean) => {
        if (!nextChecked && isChecked && selectedKpiCount === 1) {
            setShowMinMetricPopover(true);
            return;
        }

        setShowMinMetricPopover(false);
        onToggle(option.id);
    };

    return (
        <>
            <Global styles={metricSelectionPopoverGlobalStyles} />
            <Popover
                open={showMinMetricPopover}
                onOpenChange={setShowMinMetricPopover}
                placement="top"
                trigger={[]}
                overlayClassName={METRIC_SELECTION_POPOVER_OVERLAY_CLASS}
                content={<MinMetricSelectionPopoverContent onClose={() => setShowMinMetricPopover(false)} />}
                overlayInnerStyle={{ padding: 12, borderRadius: 8 }}
            >

                <Popover
                    placement="top"
                    trigger={isMaxSelectionReached ? ['hover'] : []}
                    mouseEnterDelay={MAX_METRIC_HOVER_ENTER_DELAY_SECONDS}
                    mouseLeaveDelay={MAX_METRIC_HOVER_LEAVE_DELAY_SECONDS}
                    overlayClassName={METRIC_SELECTION_POPOVER_OVERLAY_CLASS}
                    content={<MaxMetricSelectionPopoverContent />}
                    overlayInnerStyle={{ padding: 12, borderRadius: 8 }}
                >
                    <span className="inline-flex" data-testid={testId}>
                        <CustomCheckbox
                            checked={isChecked}
                            disabled={isMaxSelectionReached}
                            onChange={(event: CheckboxChangeEvent) => handleChange(event.target.checked)}
                        >
                            {option.label}
                        </CustomCheckbox>
                    </span>
                </Popover>
            </Popover>
        </>
    );
};
